"use client";
import { Button } from "@/components/ui/button";
import {
  Action,
  ControlType,
} from "@/context/AnalyticsProvider/AnalyticsProvider";
import { GamePageSection } from "@/context/AnalyticsProvider/gamePage";
import { useGameDetailsContext } from "@/context/GameDetailsContext";
import { useGameOnboardingContext } from "@/context/GameOnboardingContext";
import { trackPixelEvent } from "@/utils/pixel";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { GPOnboardingStepProps } from "./utils";

export const WishlistSteam = ({
  onNext,
  isVisible,
  track,
}: GPOnboardingStepProps) => {
  const { startSocialQuest, validateSocialQuest, questState } =
    useGameOnboardingContext();
  const { allQuests, gameData } = useGameDetailsContext();
  const steamWishlistQuest = useMemo(
    () => allQuests.find((quest) => quest.type === "steam_wishlist"),
    [allQuests],
  );
  const [openedSteamWishlist, setOpenedSteamWishlist] = useState(false);
  // TODO: return metadata from quests API and get this dynamically
  const steamAppId = 3760100;

  const handleClick = useCallback(async () => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "steam-wishlist",
        controlType: ControlType.BUTTON,
        controlLabel: "Wishlist on Steam",
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });
    if (!steamWishlistQuest) return;
    if (!openedSteamWishlist) {
      trackPixelEvent("SteamWishlistCTA", {});
      const steamWebUrl = `https://store.steampowered.com/app/${steamAppId}?utm_source=play&utm_campaign=onboarding`;
      const width = 800;
      const height = 600;
      const left = (screen.width - width) / 2;
      const top = (screen.height - height) / 2;
      window.open(
        steamWebUrl,
        "steam-wishlist",
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`,
      );
      track({
        action: Action.VIEWED,
        extras: {
          control: "steam-wishlist",
          controlType: ControlType.PAGE,
          section: GamePageSection.ONBOARDING_MODAL,
        },
      });
      setOpenedSteamWishlist(true);
      return;
    }

    const following = await validateSocialQuest(gameData);
    if (following) {
      track({
        action: Action.SUCCEEDED,
        extras: {
          control: "steam-wishlist",
          section: GamePageSection.ONBOARDING_MODAL,
        },
      });
      onNext();
    } else {
      setOpenedSteamWishlist(false);
    }
  }, [
    steamWishlistQuest,
    openedSteamWishlist,
    validateSocialQuest,
    gameData,
    track,
    onNext,
  ]);

  useEffect(() => {
    if (!questState?.error) return;
    track({
      action: Action.FAILED,
      extras: {
        control: "steam-wishlist",
        controlLabel: questState.error,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });
  }, [questState, track]);

  return (
    <div className="flex-grow flex flex-col items-center justify-between gap-y-10">
      {/* Title */}
      <div>
        <h2 className="text-[clamp(1.25rem,6.25vw,1.875rem)] font-bold leading-[clamp(1.5rem,6.25vw,2.25rem)] text-center">
          Wishlist the game on Steam
        </h2>
      </div>

      {/* Buttons */}
      <div className="flex flex-col items-center gap-y-10">
        {questState?.error ? (
          <p className="text-sm text-red-500">
            Make sure you have wishlisted. If you have wishlisted make sure your
            Steam profile is set to public.
          </p>
        ) : null}
        <Button
          size="lg"
          className="h-12 px-6 rounded-[26px] text-lg font-medium shadow-[0px_1px_3px_0px_rgba(0,0,0,0.16),0px_1px_3px_0px_rgba(0,0,0,0.32)]"
          onClick={handleClick}
        >
          {openedSteamWishlist ? "Verify Wishlist" : "Wishlist on Steam"}
        </Button>
        {/* <Button
          variant="ghost"
          size="lg"
          className="h-12 px-6 text-sm text-muted-foreground"
          onClick={onNext}
        >
          Not now
        </Button> */}
      </div>
    </div>
  );
};
